import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowLeft, Star, Clock, CheckCircle, WhatsappLogo } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';

const TOTAL_STEPS = 7;

const reviews = [
  {
    name: "Oscar D.",
    role: "Cliente",
    text: "Mi pareja y yo necesitábamos una hipoteca y ningún banco nos quería dar esa oportunidad por ser demasiado 'jóvenes'. Estamos muy contentos con Ferran, porque gracias a él no tuvimos que preocuparnos por la gestión. Finalmente nos pudo conseguir una buena hipoteca haciendo un trabajo espléndido.",
    rating: 5,
  },
  {
    name: "María L.",
    role: "Cliente",
    text: "Increíble experiencia. Pensaba que conseguir una hipoteca siendo autónoma sería imposible, pero el equipo de ÁGORA me consiguió las mejores condiciones del mercado. 100% recomendable.",
    rating: 5,
  },
  {
    name: "Carlos R.",
    role: "Cliente",
    text: "Profesionales de principio a fin. Me explicaron cada paso del proceso y negociaron condiciones que yo solo no habría conseguido. La cuota me quedó 150€ más baja de lo que me ofrecían los bancos.",
    rating: 5,
  },
  {
    name: "Ana P.",
    role: "Cliente",
    text: "Después de que 3 bancos me rechazaran, ÁGORA consiguió aprobarme la hipoteca en menos de 2 semanas. No puedo estar más agradecida por su trabajo y dedicación.",
    rating: 5,
  },
];

const comunidadesAutonomas = [
  { value: "andalucia", label: "Andalucía" },
  { value: "aragon", label: "Aragón" },
  { value: "asturias", label: "Asturias" },
  { value: "baleares", label: "Islas Baleares" },
  { value: "canarias", label: "Canarias" },
  { value: "cantabria", label: "Cantabria" },
  { value: "castilla-leon", label: "Castilla y León" },
  { value: "castilla-mancha", label: "Castilla-La Mancha" },
  { value: "cataluna", label: "Cataluña" },
  { value: "valencia", label: "Comunidad Valenciana" },
  { value: "extremadura", label: "Extremadura" },
  { value: "galicia", label: "Galicia" },
  { value: "madrid", label: "Madrid" },
  { value: "murcia", label: "Murcia" },
  { value: "navarra", label: "Navarra" },
  { value: "pais-vasco", label: "País Vasco" },
  { value: "rioja", label: "La Rioja" },
  { value: "ceuta", label: "Ceuta" },
  { value: "melilla", label: "Melilla" },
];

export const AsesoramientoWizard = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [currentReview, setCurrentReview] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    busquedaVivienda: '',
    precioVivienda: 200000,
    ahorros: 30000,
    ubicacion: '',
    situacionLaboral: '',
    ingresosFamiliares: 4000,
    nombre: '',
    email: '',
    telefono: '',
    aceptaPrivacidad: false,
    aceptaComunicaciones: false,
  });

  // Auto-rotate reviews
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      setCurrentReview((prev) => (prev + 1) % reviews.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isOpen]);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setSubmitted(false);
      setFormData({
        busquedaVivienda: '',
        precioVivienda: 200000,
        ahorros: 30000,
        ubicacion: '',
        situacionLaboral: '',
        ingresosFamiliares: 4000,
        nombre: '',
        email: '',
        telefono: '',
        aceptaPrivacidad: false,
        aceptaComunicaciones: false,
      });
    }
  }, [isOpen]);

  const progress = Math.round((currentStep / TOTAL_STEPS) * 100);

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.busquedaVivienda !== '';
      case 2: return formData.precioVivienda >= 50000;
      case 3: return formData.ahorros >= 1000;
      case 4: return formData.ubicacion !== '';
      case 5: return formData.situacionLaboral !== '';
      case 6: return formData.ingresosFamiliares >= 1000;
      case 7: return formData.nombre && formData.email && formData.telefono && formData.aceptaPrivacidad;
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS && canProceed()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!canProceed()) return;
    setIsSubmitting(true);
    console.log("📤 Submitting wizard form...");
    
    try {
      // Use relative URL to ensure it works on any domain
      const API_URL = '';
      console.log("📡 API URL: Using relative path (same domain)");
      
      const payload = {
        nombre: formData.nombre,
        email: formData.email,
        telefono: formData.telefono,
        busqueda_vivienda: formData.busquedaVivienda,
        precio_vivienda: formData.precioVivienda,
        ahorros: formData.ahorros,
        ubicacion: formData.ubicacion,
        situacion_laboral: formData.situacionLaboral,
        ingresos_familiares: formData.ingresosFamiliares,
        acepta_privacidad: formData.aceptaPrivacidad,
        acepta_comunicaciones: formData.aceptaComunicaciones,
        origen: "Wizard Asesoramiento Web",
      };
      console.log("📦 Payload:", payload);
      
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      console.log("📥 Response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ HubSpot error:", errorText);
        throw new Error('Error al enviar el formulario');
      }
      
      const data = await response.json();
      console.log("✅ HubSpot success:", data);
      setSubmitted(true);
    } catch (error) {
      console.error('❌ Wizard submission error:', error);
      // Show error toast instead of silently showing success
      alert('Hubo un error al enviar. Por favor, inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOptionSelect = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Auto-advance for option selections
    setTimeout(() => {
      if (currentStep < TOTAL_STEPS) {
        setCurrentStep(currentStep + 1);
      }
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-[#0B1220] w-full max-w-6xl h-[90vh] max-h-[700px] rounded-3xl overflow-hidden flex shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="asesoramiento-wizard"
        >
          {/* Left Panel - Branding & Reviews */}
          <div className="hidden lg:flex flex-col w-[400px] bg-[#0B1220] p-8 border-r border-white/10">
            {/* Logo */}
            <div className="flex items-center gap-2 mb-8">
              <div className="w-10 h-10 bg-[#C9A24D] rounded-lg flex items-center justify-center">
                <span className="text-[#0B1220] font-bold text-lg">A</span>
              </div>
              <span className="text-white font-bold text-lg tracking-wide">HIPOTECAS</span>
            </div>

            {/* Heading */}
            <h2 
              className="text-4xl font-bold text-white mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Tu <span className="text-[#C9A24D]">hipoteca</span>,
            </h2>
            <h2 
              className="text-4xl font-bold text-[#C9A24D] mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              tu elección
            </h2>
            <p className="text-[#9CA3AF] mb-8">
              Te acompañamos en todo el proceso y conseguimos la hipoteca ideal para ti.
            </p>

            {/* Trust Badges */}
            <div className="flex gap-4 mb-8">
              <div className="bg-[#1a2a3f] border border-white/10 text-white px-4 py-3 rounded-xl flex items-center gap-2">
                <Clock size={20} className="text-[#C9A24D]" />
                <div>
                  <p className="text-[#C9A24D] font-bold text-sm">24h</p>
                  <p className="text-xs text-white/80">Primera propuesta</p>
                </div>
              </div>
              <div className="bg-[#1a2a3f] border border-white/10 px-4 py-3 rounded-xl">
                <p className="text-white font-bold text-sm">Aprobaciones</p>
                <p className="text-[#C9A24D] font-bold">+95% de éxito</p>
              </div>
            </div>

            {/* Reviews Carousel */}
            <div className="flex-1 flex flex-col justify-end">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentReview}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="bg-[#1a2a3f] border border-white/10 rounded-2xl p-6"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-bold text-white">{reviews[currentReview].name}</p>
                      <p className="text-sm text-[#9CA3AF]">{reviews[currentReview].role}</p>
                    </div>
                    <span className="text-[#C9A24D] text-3xl font-serif">"</span>
                  </div>
                  <p className="text-[#9CA3AF] text-sm leading-relaxed mb-4">
                    {reviews[currentReview].text}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} weight="fill" className="text-[#C9A24D]" />
                      ))}
                    </div>
                    <div className="flex gap-2">
                      {reviews.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentReview(i)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            i === currentReview ? 'bg-[#C9A24D] w-4' : 'bg-white/30'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Right Panel - Form Steps */}
          <div className="flex-1 bg-[#FAF7F2] flex flex-col relative">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-[#6B7280] hover:text-[#0B1220] transition-colors z-10"
              data-testid="wizard-close-btn"
            >
              <X size={24} />
            </button>

            {/* Header with progress */}
            <div className="p-6 pb-0">
              <div className="flex items-center justify-between mb-4">
                {currentStep > 1 && !submitted && (
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 text-[#6B7280] hover:text-[#0B1220] transition-colors"
                    data-testid="wizard-back-btn"
                  >
                    <ArrowLeft size={20} />
                    <span>Atrás</span>
                  </button>
                )}
                {currentStep === 1 && <div />}
                {!submitted && (
                  <span className="text-[#6B7280] font-medium">Paso {currentStep} de {TOTAL_STEPS}</span>
                )}
              </div>

              {/* Progress Bar */}
              {!submitted && (
                <div className="mb-2">
                  <div className="h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-[#C9A24D]"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                  <p className="text-[#C9A24D] text-sm font-medium text-center mt-2">{progress}%</p>
                </div>
              )}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <AnimatePresence mode="wait">
                {submitted ? (
                  <SuccessScreen onClose={onClose} />
                ) : (
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                    className="h-full"
                  >
                    {currentStep === 1 && (
                      <Step1 
                        value={formData.busquedaVivienda} 
                        onSelect={(v) => handleOptionSelect('busquedaVivienda', v)} 
                      />
                    )}
                    {currentStep === 2 && (
                      <Step2 
                        value={formData.precioVivienda} 
                        onChange={(v) => setFormData({ ...formData, precioVivienda: v })}
                        onNext={handleNext}
                        canProceed={canProceed()}
                      />
                    )}
                    {currentStep === 3 && (
                      <Step3 
                        value={formData.ahorros} 
                        onChange={(v) => setFormData({ ...formData, ahorros: v })}
                        onNext={handleNext}
                        canProceed={canProceed()}
                      />
                    )}
                    {currentStep === 4 && (
                      <Step4 
                        value={formData.ubicacion} 
                        onSelect={(v) => setFormData({ ...formData, ubicacion: v })}
                        onNext={handleNext}
                        canProceed={canProceed()}
                      />
                    )}
                    {currentStep === 5 && (
                      <Step5 
                        value={formData.situacionLaboral} 
                        onSelect={(v) => handleOptionSelect('situacionLaboral', v)} 
                      />
                    )}
                    {currentStep === 6 && (
                      <Step6 
                        value={formData.ingresosFamiliares} 
                        onChange={(v) => setFormData({ ...formData, ingresosFamiliares: v })}
                        onNext={handleNext}
                        canProceed={canProceed()}
                      />
                    )}
                    {currentStep === 7 && (
                      <Step7 
                        formData={formData}
                        onChange={(field, value) => setFormData({ ...formData, [field]: value })}
                        onSubmit={handleSubmit}
                        isSubmitting={isSubmitting}
                        canProceed={canProceed()}
                      />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Step 1: ¿Cómo llevas la búsqueda de vivienda?
const Step1 = ({ value, onSelect }) => (
  <div>
    <h3 
      className="text-2xl md:text-3xl font-bold text-[#0B1220] mb-2"
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      ¿Cómo llevas la búsqueda de vivienda?
    </h3>
    <p className="text-[#6B7280] mb-8">Elige una opción</p>
    
    <div className="space-y-4">
      {[
        { value: 'elegida', label: 'Tengo la vivienda elegida' },
        { value: 'reservada', label: 'Tengo la vivienda reservada / Arras' },
        { value: 'busqueda', label: 'Estoy en búsqueda' },
      ].map((option) => (
        <button
          key={option.value}
          onClick={() => onSelect(option.value)}
          className={`w-full p-5 text-left rounded-2xl border-2 transition-all ${
            value === option.value
              ? 'border-[#C9A24D] bg-[#C9A24D]/5'
              : 'border-[#E5E7EB] hover:border-[#C9A24D]/50'
          }`}
          data-testid={`step1-option-${option.value}`}
        >
          <span className="text-[#0B1220] font-medium">{option.label}</span>
        </button>
      ))}
    </div>
  </div>
);

// Step 2: ¿Cuál es el precio de la vivienda?
const Step2 = ({ value, onChange, onNext, canProceed }) => (
  <div>
    <h3 
      className="text-2xl md:text-3xl font-bold text-[#0B1220] mb-2"
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      ¿Cuál es el precio de la vivienda?
    </h3>
    <p className="text-[#6B7280] mb-8">Elige una opción</p>
    
    <div className="relative mb-6">
      <Input
        type="text"
        value={value.toLocaleString('es-ES')}
        onChange={(e) => {
          const num = parseInt(e.target.value.replace(/\./g, '')) || 50000;
          onChange(Math.min(Math.max(num, 50000), 1500000));
        }}
        className="w-full h-14 text-xl pr-12 rounded-2xl border-2 border-[#E5E7EB] focus:border-[#C9A24D]"
        placeholder="Ej: 120.000"
        data-testid="step2-price-input"
      />
      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B7280] font-medium">€</span>
    </div>

    <Slider
      value={[value]}
      onValueChange={([v]) => onChange(v)}
      min={50000}
      max={1500000}
      step={5000}
      className="mb-4"
    />
    <div className="flex justify-between text-sm text-[#6B7280]">
      <span>50.000€</span>
      <span>1.500.000€</span>
    </div>

    <div className="mt-8">
      <Button
        onClick={onNext}
        disabled={!canProceed}
        className="w-full h-14 bg-[#C9A24D] hover:bg-[#D4AF5A] text-[#0B1220] font-semibold rounded-2xl disabled:opacity-50"
        data-testid="step2-next-btn"
      >
        Continuar
      </Button>
    </div>
  </div>
);

// Step 3: ¿Cuántos ahorros puedes aportar?
const Step3 = ({ value, onChange, onNext, canProceed }) => (
  <div>
    <h3 
      className="text-2xl md:text-3xl font-bold text-[#0B1220] mb-2"
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      ¿Cuántos ahorros puedes aportar?
    </h3>
    <p className="text-[#6B7280] mb-8">Elige una opción</p>
    
    <div className="relative mb-6">
      <Input
        type="text"
        value={value.toLocaleString('es-ES')}
        onChange={(e) => {
          const num = parseInt(e.target.value.replace(/\./g, '')) || 1000;
          onChange(Math.min(Math.max(num, 1000), 1000000));
        }}
        className="w-full h-14 text-xl pr-12 rounded-2xl border-2 border-[#E5E7EB] focus:border-[#C9A24D]"
        placeholder="Ej: 30.000"
        data-testid="step3-savings-input"
      />
      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B7280] font-medium">€</span>
    </div>

    <Slider
      value={[value]}
      onValueChange={([v]) => onChange(v)}
      min={1000}
      max={1000000}
      step={1000}
      className="mb-4"
    />
    <div className="flex justify-between text-sm text-[#6B7280]">
      <span>1.000€</span>
      <span>1.000.000€</span>
    </div>

    <div className="mt-8">
      <Button
        onClick={onNext}
        disabled={!canProceed}
        className="w-full h-14 bg-[#C9A24D] hover:bg-[#D4AF5A] text-[#0B1220] font-semibold rounded-2xl disabled:opacity-50"
        data-testid="step3-next-btn"
      >
        Continuar
      </Button>
    </div>
  </div>
);

// Step 4: Ubicación de la propiedad
const Step4 = ({ value, onSelect, onNext, canProceed }) => {
  return (
    <div>
      <h3 
        className="text-2xl md:text-3xl font-bold text-[#0B1220] mb-2"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        Ubicación de la propiedad
      </h3>
      <p className="text-[#6B7280] mb-4">Elige tu comunidad autónoma</p>
      
      <div className="max-h-[280px] overflow-y-auto pr-2 space-y-2 custom-scrollbar">
        {comunidadesAutonomas.map((ccaa) => (
          <button
            key={ccaa.value}
            onClick={() => onSelect(ccaa.value)}
            className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
              value === ccaa.value
                ? 'border-[#C9A24D] bg-[#C9A24D]/10'
                : 'border-[#E5E7EB] hover:border-[#C9A24D]/50'
            }`}
            data-testid={`step4-region-${ccaa.value}`}
          >
            <span className="text-[#0B1220] font-medium">{ccaa.label}</span>
          </button>
        ))}
      </div>

      <div className="mt-6">
        <Button
          onClick={onNext}
          disabled={!canProceed}
          className="w-full h-14 bg-[#C9A24D] hover:bg-[#D4AF5A] text-[#0B1220] font-semibold rounded-2xl disabled:opacity-50"
          data-testid="step4-next-btn"
        >
          Continuar
        </Button>
      </div>
    </div>
  );
};

// Step 5: ¿Cuál es tu situación laboral?
const Step5 = ({ value, onSelect }) => (
  <div>
    <h3 
      className="text-2xl md:text-3xl font-bold text-[#0B1220] mb-2"
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      ¿Cuál es tu situación laboral?
    </h3>
    <p className="text-[#6B7280] mb-8">Elige una opción</p>
    
    <div className="space-y-3">
      {[
        { value: 'indefinido', label: 'Indefinido' },
        { value: 'funcionario', label: 'Funcionario' },
        { value: 'autonomo', label: 'Autónomo' },
        { value: 'temporal', label: 'Temporal' },
        { value: 'pensionista', label: 'Pensionista' },
      ].map((option) => (
        <button
          key={option.value}
          onClick={() => onSelect(option.value)}
          className={`w-full p-5 text-left rounded-2xl border-2 transition-all ${
            value === option.value
              ? 'border-[#C9A24D] bg-[#C9A24D]/5'
              : 'border-[#E5E7EB] hover:border-[#C9A24D]/50'
          }`}
          data-testid={`step5-option-${option.value}`}
        >
          <span className="text-[#0B1220] font-medium">{option.label}</span>
        </button>
      ))}
    </div>
  </div>
);

// Step 6: ¿Cuáles son los ingresos de la unidad familiar?
const Step6 = ({ value, onChange, onNext, canProceed }) => (
  <div>
    <h3 
      className="text-2xl md:text-3xl font-bold text-[#0B1220] mb-2"
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      ¿Cuáles son los ingresos de la unidad familiar?
    </h3>
    <p className="text-[#6B7280] mb-8">Indica la suma de los ingresos de todos los solicitantes de la hipoteca</p>
    
    <div className="relative mb-6">
      <Input
        type="text"
        value={value.toLocaleString('es-ES')}
        onChange={(e) => {
          const num = parseInt(e.target.value.replace(/\./g, '')) || 1000;
          onChange(Math.min(Math.max(num, 1000), 20000));
        }}
        className="w-full h-14 text-xl pr-20 rounded-2xl border-2 border-[#E5E7EB] focus:border-[#C9A24D]"
        placeholder="Ej: 4.000"
        data-testid="step6-income-input"
      />
      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B7280] font-medium">€/mes</span>
    </div>

    <Slider
      value={[value]}
      onValueChange={([v]) => onChange(v)}
      min={1000}
      max={20000}
      step={100}
      className="mb-4"
    />
    <div className="flex justify-between text-sm text-[#6B7280]">
      <span>1.000€</span>
      <span>20.000€</span>
    </div>

    <div className="mt-8">
      <Button
        onClick={onNext}
        disabled={!canProceed}
        className="w-full h-14 bg-[#C9A24D] hover:bg-[#D4AF5A] text-[#0B1220] font-semibold rounded-2xl disabled:opacity-50"
        data-testid="step6-next-btn"
      >
        Continuar
      </Button>
    </div>
  </div>
);

// Step 7: Formulario de contacto
const Step7 = ({ formData, onChange, onSubmit, isSubmitting, canProceed }) => (
  <div>
    <h3 
      className="text-2xl md:text-3xl font-bold text-[#0B1220] mb-2"
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      Último paso
    </h3>
    <p className="text-[#6B7280] mb-6">Déjanos tus datos y te contactaremos en menos de 24h</p>
    
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-[#0B1220] mb-2">Nombre completo</label>
        <Input
          type="text"
          value={formData.nombre}
          onChange={(e) => onChange('nombre', e.target.value)}
          className="w-full h-12 rounded-xl border-2 border-[#E5E7EB] focus:border-[#C9A24D]"
          placeholder="Tu nombre"
          data-testid="step7-name-input"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[#0B1220] mb-2">Email</label>
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => onChange('email', e.target.value)}
          className="w-full h-12 rounded-xl border-2 border-[#E5E7EB] focus:border-[#C9A24D]"
          placeholder="tu@email.com"
          data-testid="step7-email-input"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[#0B1220] mb-2">Teléfono</label>
        <Input
          type="tel"
          value={formData.telefono}
          onChange={(e) => onChange('telefono', e.target.value)}
          className="w-full h-12 rounded-xl border-2 border-[#E5E7EB] focus:border-[#C9A24D]"
          placeholder="+34 676 280 792"
          data-testid="step7-phone-input"
        />
      </div>

      {/* Privacy checkboxes */}
      <div className="space-y-3 pt-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={formData.aceptaPrivacidad}
            onCheckedChange={(checked) => onChange('aceptaPrivacidad', checked)}
            className="mt-0.5"
            data-testid="step7-privacy-checkbox"
          />
          <span className="text-sm text-[#6B7280]">
            He leído y acepto la{' '}
            <a href="/politica-privacidad" target="_blank" className="text-[#C9A24D] hover:underline">
              Política de Privacidad
            </a>{' '}
            *
          </span>
        </label>
        
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={formData.aceptaComunicaciones}
            onCheckedChange={(checked) => onChange('aceptaComunicaciones', checked)}
            className="mt-0.5"
            data-testid="step7-communications-checkbox"
          />
          <span className="text-sm text-[#6B7280]">
            Acepto recibir comunicaciones comerciales de ÁGORA HIPOTECAS
          </span>
        </label>
      </div>
    </div>

    <div className="mt-6">
      <Button
        onClick={onSubmit}
        disabled={!canProceed || isSubmitting}
        className="w-full h-14 bg-[#C9A24D] hover:bg-[#D4AF5A] text-[#0B1220] font-semibold rounded-2xl disabled:opacity-50"
        data-testid="step7-submit-btn"
      >
        {isSubmitting ? 'Enviando...' : 'Solicitar asesoramiento gratuito'}
      </Button>
    </div>
  </div>
);

// Success Screen
const SuccessScreen = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    className="h-full flex flex-col items-center justify-center text-center px-4"
  >
    <div className="w-20 h-20 bg-[#C9A24D]/20 rounded-full flex items-center justify-center mb-6">
      <CheckCircle size={48} className="text-[#C9A24D]" weight="fill" />
    </div>
    <h3 
      className="text-2xl md:text-3xl font-bold text-[#0B1220] mb-3"
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      ¡Solicitud enviada!
    </h3>
    <p className="text-[#6B7280] mb-8 max-w-md">
      Gracias por confiar en ÁGORA HIPOTECAS. Un asesor especializado te contactará en menos de 24 horas.
    </p>
    
    <div className="space-y-3 w-full max-w-xs">
      <Button
        onClick={onClose}
        className="w-full h-12 bg-[#C9A24D] hover:bg-[#D4AF5A] text-[#0B1220] font-semibold rounded-xl"
        data-testid="success-close-btn"
      >
        Volver a la web
      </Button>
      <a
        href="https://wa.me/34676280792?text=Hola!%20Quiero%20mi%20estudio%20financiero%20gratuito%20y%20recibir%20asesoramiento%20personalizado%20sobre%20mi%20hipoteca."
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full h-12 bg-[#0B1220] hover:bg-[#1a2a3f] text-white font-semibold rounded-xl transition-colors border border-white/10"
        data-testid="success-whatsapp-btn"
      >
        <WhatsappLogo size={20} weight="fill" />
        Contactar por WhatsApp
      </a>
    </div>
  </motion.div>
);

export default AsesoramientoWizard;
